import React from 'react';
import PropTypes from 'prop-types';

import { normalizationAmount } from '../../../../util/normalization';
import Dropdown from '../../../../components/Dropdown';

import { ClusterBarChart } from './ClusterViewCharts';

import style from './index.scss';

/**
 * NoDataPanel
 * @param onTogglePanelVisibility
 * @param onReload
 * @returns {*}
 * @constructor
 */
const NoDataPanel = ({ onTogglePanelVisibility, onReload }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Cluster View by Selected Segment</p>
          </div>

          <div className="offset-md-4 col-md-2">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>No Cluster View data exists for the period selected.</p>
            <p>
              <a className={style.retryLink} onClick={onReload}>
                Click here to fetch cluster view data again.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * IsLoadingPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const IsLoadingPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Cluster View by Selected Segment</p>
          </div>

          <div className="offset-md-4 col-md-2">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>Loading Cluster View chart data...</p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * MinimizedPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const MinimizedPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Cluster View by Selected Segment</p>
          </div>

          <div className="offset-md-4 col-md-2">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-maximize"></i>
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * ClusterViewChart
 * @param props
 * @returns {*}
 * @constructor
 */
export const ClusterViewChart = (props) => {
  const {
    data,
    isLoading,
    isCollapsed,
    onTogglePanelVisibility,
    onReload,
    onAxisChange,
    selectedAxis
  } = props;

  if (isCollapsed) {
    return <MinimizedPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (isLoading) {
    return <IsLoadingPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (Array.isArray(data) && data.length > 0) {
    const formattedData = [];
    data.forEach((o, index) => {
      // produce unique suffix for repeating items
      let suffixUniqueKey = '';
      let suffixCount = 0;
      formattedData.forEach((item) => {
        if (item.clusterId === o.clusterId) {
          suffixCount++;
        }
      });

      if (suffixCount > 0) {
        suffixUniqueKey = `(${suffixCount})`;
      }

      let axis = null;
      let attributes = null;

      try {
        attributes = JSON.parse(o.attributes);
      } catch (err) {}

      try {
        axis = JSON.parse(o.riskViewList);
      } catch (err) {}

      formattedData.push({
        ...o,
        id: `${o.clusterId}${suffixUniqueKey}`,
        count: o.clusterSize,
        attributes,
        historicalAlertCount: o.historicalAlertCount,
        historicalStrCount: o.historicalStrCount,
        axis
      });
    });

    const dropdownSelections =
      Array.isArray(formattedData) && formattedData[0]?.axis
        ? Object.keys(formattedData[0].axis).map((axis) => {
            return { id: axis, name: axis, value: axis };
          })
        : [];
    const updatedData =
      Array.isArray(formattedData) && formattedData.length > 0
        ? formattedData.map((cluster) => {
            return { ...cluster, ...cluster.axis };
          })
        : [];
    const listOfSelectedAxis =
      Array.isArray(formattedData) && formattedData.length > 0
        ? updatedData.map((cluster) => {
            const clusterValue =
              cluster[
                selectedAxis
                  ? selectedAxis.value
                  : dropdownSelections[0]
                  ? dropdownSelections[0].value
                  : ''
              ];
            return clusterValue !== undefined && clusterValue !== null
              ? clusterValue
              : 0;
          })
        : [];

    const normalizedAmounts = normalizationAmount(listOfSelectedAxis);

    const normalizedClusters = updatedData.map((cluster, idx) => {
      return { ...cluster, normalizedAxisData: normalizedAmounts[idx] };
    });

    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>Cluster View by Selected Segment</p>
            </div>

            <div className="offset-md-4 col-md-2">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-minimize"></i>
              </h5>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-12">
              <div className="pull-right">
                <h4>Total number of clusters: {data.length}</h4>
                <Dropdown
                  dropdownValue={
                    selectedAxis
                      ? selectedAxis
                      : dropdownSelections[0]
                      ? dropdownSelections[0]
                      : null
                  }
                  dropdownOptions={dropdownSelections}
                  handleValueChange={onAxisChange}
                />
              </div>
            </div>

            <div className="col-md-12">
              <ClusterBarChart
                data={normalizedClusters}
                dataKeyNormalized="normalizedAxisData"
                dataKey={
                  selectedAxis
                    ? selectedAxis.value
                    : dropdownSelections[0]
                    ? dropdownSelections[0].value
                    : null
                }
                xDataKey="name"
                xLabel="Clusters (Arranged by count from left to right)"
                yLabel={
                  selectedAxis
                    ? selectedAxis.value
                    : dropdownSelections[0]
                    ? dropdownSelections[0].value
                    : null
                }
                width={innerWidth / 1.2}
                height={innerWidth / 3}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NoDataPanel
      onReload={onReload}
      onTogglePanelVisibility={onTogglePanelVisibility}
    />
  );
};

ClusterViewChart.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onTogglePanelVisibility: PropTypes.func,
  onReload: PropTypes.func,
  onAxisChange: PropTypes.func,
  selectedCustomerClusterAxis: PropTypes.number
};
